import React from 'react';
import { Star, Users, MapPin, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useGooglePlacesSummary } from '@/hooks/useGooglePlacesSummary';

const GooglePlacesSummary: React.FC = () => {
  const { data, loading, error } = useGooglePlacesSummary();

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-48 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || data.totalSites === 0) {
    return null;
  }

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative w-6 h-6">
            <Star className="w-6 h-6 text-gray-300 absolute" />
            <div className="overflow-hidden w-1/2">
              <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
            </div>
          </div>
        );
      } else {
        stars.push(
          <Star key={i} className="w-6 h-6 text-gray-300" />
        );
      }
    }

    return stars;
  };

  return (
    <div className="bg-gradient-to-r from-yellow-50 via-orange-50 to-yellow-50 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-800 mb-3">
            Nos clients nous font confiance
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Découvrez ce que pensent nos clients de nos services à travers leurs avis Google
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Note moyenne */}
            <Card className="bg-white/80 backdrop-blur border-yellow-200 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-3">
                  {data.averageRating > 0 && renderStars(data.averageRating)}
                </div>
                <div className="text-3xl font-bold text-gray-800 mb-1">
                  {data.averageRating > 0 ? data.averageRating.toFixed(1) : 'N/A'}
                </div>
                <div className="text-sm text-gray-600 font-medium">
                  Note moyenne
                </div>
              </CardContent>
            </Card>

            {/* Total des avis */}
            <Card className="bg-white/80 backdrop-blur border-orange-200 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-3">
                  <Users className="w-8 h-8 text-orange-500" />
                </div>
                <div className="text-3xl font-bold text-gray-800 mb-1">
                  {data.totalReviews.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600 font-medium">
                  Avis clients
                </div>
              </CardContent>
            </Card>

            {/* Sites avec avis */}
            <Card className="bg-white/80 backdrop-blur border-green-200 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-3">
                  <MapPin className="w-8 h-8 text-green-500" />
                </div>
                <div className="text-3xl font-bold text-gray-800 mb-1">
                  {data.sitesWithRatings}
                </div>
                <div className="text-sm text-gray-600 font-medium">
                  Sites évalués
                </div>
              </CardContent>
            </Card>

            {/* Taux de satisfaction */}
            <Card className="bg-white/80 backdrop-blur border-blue-200 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-3">
                  <TrendingUp className="w-8 h-8 text-blue-500" />
                </div>
                <div className="text-3xl font-bold text-gray-800 mb-1">
                  {data.averageRating > 0 ? Math.round((data.averageRating / 5) * 100) : 0}%
                </div>
                <div className="text-sm text-gray-600 font-medium">
                  Satisfaction
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Message de synthèse */}
          {data.averageRating > 0 && (
            <div className="text-center bg-white/60 backdrop-blur rounded-2xl p-6 shadow-lg">
              <p className="text-lg text-gray-700 leading-relaxed">
                <span className="font-semibold text-yellow-600">
                  {data.totalReviews.toLocaleString()} clients satisfaits
                </span>
                {' '}nous ont fait confiance et ont partagé leur expérience avec une note moyenne de{' '}
                <span className="font-bold text-yellow-600">
                  {data.averageRating.toFixed(1)}/5 étoiles
                </span>
                {' '}sur nos {data.sitesWithRatings} centres évalués.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GooglePlacesSummary;